import React, { useState, useEffect } from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import { ResponsiveImage, Modal, ImageCarousel } from '../../components';
import ActionBarMaybe from './ActionBarMaybe';
import clsx from 'clsx';

import css from './ListingPage.module.css';
import variables from '../../styles/globals.module.scss';

import {
  TourlyResponsiveImage,
  StandardDPRVariants,
  PreloadHighPriorityImage,
  LISTING_CARD_VARIANTS,
  HERO_SIZES,
  processVariantsForImage,
  getTourlyProxyServer,
} from '../../util/responsiveImagesHelper';

import { useViewportSize } from '@mantine/hooks';

const DESKTOP_VIEWPORT = 767; // viewports below this will show a single image

const SectionImages = (props) => {
  const {
    title,
    listing,
    isOwnListing,
    editParams,
    handleViewPhotosClick,
    imageCarouselOpen,
    onImageCarouselClose,
    onManageDisableScrolling,
    selectedImageIndex,
    serverOrigin,
  } = props;

  const { width } = useViewportSize();
  const [showMobileGallery, setShowMobileGallery] = useState(false);
  const hasImages = listing.images && listing.images.length > 0;
  const minGridImages = hasImages && listing.images.length >= 3;
  const firstImage = hasImages ? listing.images[0] : null;
  const moreThanOneImage = hasImages && listing.images.length > 1;
  let show5Images = listing.images.length > 4 ? true : false;

  const heroImageHeight = width >= 1280 ? 544 : 410;
  const smallerImagesWidth = width >= 1280 ? 400 : 300;
  const heroImageWidth = (() => {
    const margins = width >= variables.viewportLargePixels ? 36 * 2 : 24 * 2;
    const howManyImagesWillBeShown = !minGridImages
      ? 1
      : (show5Images && width >= variables.viewportLargeWithPaddingsPixels ? 5 : 3);
    let calculatedWidth = 0;
    if (!minGridImages || showMobileGallery) {
      // Hero image will be 100% wide
      calculatedWidth = Math.min(1280, width) - margins; // take away margins
    } else {
      calculatedWidth =
        Math.min(1280, width) -
        margins -
        (howManyImagesWillBeShown == 5 ? 2 * smallerImagesWidth + 2 * 10 : smallerImagesWidth + 10); // take away width of columns + gaps
    }
    return calculatedWidth;
  })();

  const heroImageCropRadius = `${heroImageWidth * 8},${heroImageHeight * 8}`;
  useEffect(() => {
    setShowMobileGallery((prevState) => {
      if (width === 0) return prevState;
      return width <= DESKTOP_VIEWPORT;
    });
  }, [width]);
  // Action bar is wrapped with a div that prevents the click events
  // to the parent that would otherwise open the image carousel
  const actionBar = listing.id ? (
    <div onClick={(e) => e.stopPropagation()}>
      <ActionBarMaybe isOwnListing={isOwnListing} listing={listing} editParams={editParams} />
    </div>
  ) : null;

  const viewPhotosButton = hasImages ? (
    <button className={css.viewPhotos} onClick={handleViewPhotosClick}>
      <FormattedMessage
        id="ListingPage.viewImagesButton"
        values={{ count: listing.images.length }}
      />
    </button>
  ) : null;

  const responsiveGridImage = (image, priority = 'auto', sizes = '100vw') => {
    return (
      <TourlyResponsiveImage
        alt={title}
        image={image}
        variants={['grid-300x200', 'grid-600x400', ...LISTING_CARD_VARIANTS]}
        sizes={sizes}
        fetchpriority={priority}
        key={Math.random(0, 1)}
      />
    );
  };

  const responsiveHeroImage = (image) => {
    const proxyServer = getTourlyProxyServer(typeof window !== 'undefined' ? window.location.origin : serverOrigin);
    const imageURL = image !== null ? (image?.attributes?.variants?.['landscape-crop4x']?.url ?? "") : '';
    const processedVariants = processVariantsForImage(
      true,
      heroImageWidth,
      heroImageHeight,
      proxyServer.toString(),
      imageURL,
      StandardDPRVariants
    );

    // const allParams =
    //   'blend-alpha=0&fit=crop&crop=edges&rect=center,middle,' +
    //   heroImageCropRadius +
    //   '&ar=' +
    //   `${heroImageWidth}:${heroImageHeight}`;
    const allParams =
      'blend-alpha=0&fit=crop&crop=edges&rect=center,middle' +
      '&ar=' +
      `${heroImageWidth}:${heroImageHeight}`;
    return (
      <>
        <PreloadHighPriorityImage
          alt="Tourly hero image"
          image={processedVariants}
          variants={['x1', 'x2']}
          sizes={HERO_SIZES}
          fetchpriority={'high'}
          loading="eager"
          additionalParams={allParams}
        />
        <TourlyResponsiveImage
          alt="Tourly hero image"
          image={processedVariants}
          variants={['x1', 'x2']}
          sizes={HERO_SIZES}
          fetchpriority={'high'}
          loading="eager"
          additionalParams={allParams}
        />
      </>
    );
  };

  const viewportMedium = variables.viewportMedium;

  const imageGrid = !showMobileGallery ? (
    <div className={css.imageGrid}>
      <div className={css.hero_image} onClick={handleViewPhotosClick} index={0}>
        {responsiveHeroImage(firstImage)}
      </div>
      {/* 3 images and more */}
      {minGridImages && (
        <>
          <div className={css.hero_imageColumn2}>
            <div className={css.inner_img} onClick={handleViewPhotosClick} index={1}>
              {responsiveGridImage(listing.images[1], 'high', `${smallerImagesWidth}px`)}
            </div>
            <div className={css.inner_img} onClick={handleViewPhotosClick} index={2}>
              {responsiveGridImage(listing.images[2], 'high', `${smallerImagesWidth}px`)}
            </div>
          </div>
          {/* show 5 images */}
          {show5Images && (
            <div className={css.hero_imageColumn3}>
              <div className={css.inner_img} onClick={handleViewPhotosClick} index={3}>
                {responsiveGridImage(listing.images[3], 'high', `${smallerImagesWidth}px`)}
              </div>
              <div className={css.inner_img} onClick={handleViewPhotosClick} index={4}>
                {responsiveGridImage(listing.images[4], 'high', `${smallerImagesWidth}px`)}
              </div>
            </div>
          )}
        </>
      )}
      {moreThanOneImage && viewPhotosButton}
    </div>
  ) : null;

  const imageCover = showMobileGallery ? (
    <div className={css.imageCover}>
      {responsiveHeroImage(firstImage)}
      {viewPhotosButton}
    </div>
  ) : null;

  const preloadImages = listing.images.map((image, index) => {
    return index < 5 ? (
      <PreloadHighPriorityImage
        key={index}
        alt="Tourly hero image"
        image={image}
        fetchpriority={'high'}
        loading="eager"
        variants={LISTING_CARD_VARIANTS}
        sizes={
          index === 0 ? (minGridImages ? `${viewportMedium} 50vw, 100vw` : '100vw') : '24.77vw'
        }
      />
    ) : null;
  });

  return width > 0 ? (
    <div className={css.sectionImages}>
      {actionBar}
      {/* {preloadImages} */}
      {imageCover}
      {imageGrid}
      <Modal
        id="ListingPage.imageCarousel"
        scrollLayerClassName={css.carouselModalScrollLayer}
        containerClassName={css.carouselModalContainer}
        lightCloseButton
        isOpen={imageCarouselOpen}
        onClose={onImageCarouselClose}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <ImageCarousel images={listing.images} selectedImageIndex={selectedImageIndex} />
      </Modal>
    </div>
  ) : null;
};

export default React.memo(SectionImages);
